// The cookie banner (see ../consent.ts): small, at the bottom, calm. Two buttons of equal weight; nothing analytic
// loads before a choice. "Cookie settings" (account menu, command palette) reopens it to change the choice.
import { useEffect, useRef, useSyncExternalStore } from "react";
import { BRAND } from "../brand.ts";
import { COOKIE_POLICY_URL, type ConsentController, type ConsentState, sharedConsent } from "../consent.ts";
import "./consent.css";

/** The consent choice and whether the banner shows, live. */
export function useConsent(c: ConsentController = sharedConsent()): ConsentState {
  return useSyncExternalStore(c.subscribe, c.getState, c.getState);
}

export function ConsentBanner() {
  const consent = sharedConsent();
  const state = useConsent(consent);
  const ref = useRef<HTMLElement>(null);
  // a reopen takes focus (the first showing does not: it must not pull a person out of what they are typing)
  useEffect(() => {
    if (state.focus > 0) ref.current?.focus();
  }, [state.focus]);
  if (!state.open || !BRAND.analytics) return null;
  const reopened = state.choice !== null;
  return (
    <section
      ref={ref}
      className="cb"
      aria-labelledby="cb-text"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === "Escape") consent.dismiss();
      }}
    >
      <p className="cb-text" id="cb-text">
        We'd like to use PostHog (EU) analytics to learn which parts of Filmsquare help you. No ads, no
        session recording. You can change this any time.
      </p>
      {reopened && (
        <p className="cb-state" role="status">
          Right now: {state.choice === "analytics" ? "analytics are on." : "analytics are off."}
        </p>
      )}
      <div className="cb-actions">
        <button type="button" className="k-btn k-outline" onClick={() => consent.choose("analytics")}>
          Accept analytics
        </button>
        <button type="button" className="k-btn k-outline" onClick={() => consent.choose("necessary")}>
          Reject
        </button>
        <a className="link cb-policy" href={COOKIE_POLICY_URL} target="_blank" rel="noopener noreferrer">
          Cookie policy
        </a>
        {reopened && (
          <button type="button" className="k-btn k-text cb-close" onClick={() => consent.dismiss()}>
            Close
          </button>
        )}
      </div>
    </section>
  );
}
